import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, Autocomplete, Box, Typography, Snackbar, Alert, MenuItem, IconButton
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import * as API from '../../api';
import CurrencyInput from '../CurrencyInput';

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

const VEHICLE_TYPE_CHOICES = [
    { value: 'SHUTTLE', label: 'Shuttle Bus' },
    { value: 'LIMO', label: 'Limousine' },
    { value: 'DCAR', label: 'Dcar' },
    { value: 'SINGLE CABIN', label: 'Cabin Đơn' },
    { value: 'COUPLE CABIN', label: 'Cabin Đôi' },
    { value: 'SINGLE SLEEPER', label: 'Giường Nằm Đơn' },
    { value: 'COUPLE SLEEPER', label: 'Giường Nằm Đôi' },
    { value: '4-SEAT TAXI', label: 'Taxi 4 Chỗ' },
    { value: '7-SEAT TAXI', label: 'Taxi 7 chỗ' }
];

const TransportRouteDialog = ({ open, onClose, initialData, onSuccess }) => {
    const defaultPoint = { province: '', address: '', time: '' };

    const [form, setForm] = useState({
        route_code: '', route_name: '', supplier: null, vehicle_type: 'LIMO',
        pickup_point: [], dropoff_point: [],
        cost_price: 0, cost_original: 0, cost_currency: 'VND', cost_rate: 1,
        selling_price: 0, selling_original: 0, selling_currency: 'VND', selling_rate: 1
    });

    const [suppliers, setSuppliers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });
    const handleCloseToast = () => setToast({ ...toast, open: false });

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                // Tải danh mục NCC
                const supRes = await API.fetchProvidersByCategory('TRANSPORT');
                const supData = supRes.data?.results ? supRes.data.results : supRes.data;
                setSuppliers(Array.isArray(supData) ? supData : []);
                
                // Tải danh mục Địa điểm (Location)
                const locRes = await API.fetchLocations(); // Đảm bảo hàm này có trong api.js
                const locData = locRes.data?.results ? locRes.data.results : locRes.data;
                // Chuyển mảng object thành mảng string tên địa điểm cho dễ chọn
                const locationNames = Array.isArray(locData) ? locData.map(loc => loc.name) : [];
                setLocations(locationNames);
            } catch (err) {
                console.error("Lỗi tải danh mục NCC:", err);
            }
        };
        fetchSuppliers();
    }, []);

    useEffect(() => {
        if (initialData) {
            setForm({
                ...initialData,
                supplier: initialData.supplier || null,
                pickup_point: Array.isArray(initialData.pickup_point) ? initialData.pickup_point : [],
                dropoff_point: Array.isArray(initialData.dropoff_point) ? initialData.dropoff_point : [],
                cost_original: initialData.cost_price || 0,
                cost_currency: 'VND', cost_rate: 1,
                selling_original: initialData.selling_price || 0,
                selling_currency: 'VND', selling_rate: 1
            });
        } else {
            setForm({
                route_code: '', route_name: '', supplier: null, vehicle_type: 'LIMO',
                pickup_point: [{ ...defaultPoint }], dropoff_point: [{ ...defaultPoint }],
                cost_price: 0, cost_original: 0, cost_currency: 'VND', cost_rate: 1,
                selling_price: 0, selling_original: 0, selling_currency: 'VND', selling_rate: 1
            });
        }
    }, [initialData, open]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // XỬ LÝ DYNAMIC LIST CHO ĐIỂM ĐÓN/TRẢ
    const handlePointChange = (type, index, field, value) => {
        const newPoints = [...form[type]];
        newPoints[index][field] = value;
        setForm({ ...form, [type]: newPoints });
    };

    const addPoint = (type) => {
        setForm({ ...form, [type]: [...form[type], { ...defaultPoint }] });
    };

    const removePoint = (type, index) => {
        const newPoints = [...form[type]];
        newPoints.splice(index, 1);
        setForm({ ...form, [type]: newPoints });
    };

    const handleMoneyChange = (fieldPrefix, data) => {
        setForm(prev => ({
            ...prev,
            [`${fieldPrefix}_price`]: data.vnd,
            [`${fieldPrefix}_original`]: data.original,
            [`${fieldPrefix}_currency`]: data.currency,
            [`${fieldPrefix}_rate`]: data.rate
        }));
    };

    const handleSubmit = async () => {
        if (!form.route_name || !form.supplier) {
            showToast("Vui lòng nhập Tên tuyến và chọn Nhà xe!", "error");
            return;
        }
        
        // Lọc bỏ các điểm rỗng (không có data)
        const cleanPoints = (points) => points.filter(p => p.province || p.address || p.time);

        const payload = {
            ...form,
            pickup_point: cleanPoints(form.pickup_point),
            dropoff_point: cleanPoints(form.dropoff_point),
            cost_price: form.cost_price, 
            selling_price: form.selling_price
        };

        if (!payload.route_code) delete payload.route_code;

        delete payload.cost_original; delete payload.cost_currency; delete payload.cost_rate;
        delete payload.selling_original; delete payload.selling_currency; delete payload.selling_rate;

        if (payload.supplier && typeof payload.supplier === 'object') payload.supplier = payload.supplier.id;

        try {
            if (initialData?.route_code) {
                await API.updateTransportRoute(initialData.route_code, payload);
            } else {
                await API.createTransportRoute(payload);
            }
            showToast("Lưu tuyến xe thành công!", "success");
            setTimeout(() => onSuccess(), 500);
        } catch (e) {
            const errMsg = e.response?.data?.detail || e.response?.data?.route_code?.[0] || e.message;
            showToast('Lỗi lưu tuyến xe: ' + errMsg, "error");
        }
    };

    const renderPointList = (type, label) => (
        <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <CustomLabel title={label} />
                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addPoint(type)}>
                    Thêm điểm
                </Button>
            </Box>
            {form[type].map((point, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                    
                    <Autocomplete
                        size="small"
                        sx={{ flex: 1.5 }}
                        options={locations}
                        value={point.province || null}
                        onChange={(e, newValue) => handlePointChange(type, index, 'province', newValue || '')}
                        freeSolo
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Chọn Địa điểm..." />
                        )}
                    />

                    <TextField size="small" placeholder="Chi tiết địa chỉ" value={point.address} sx={{ flex: 3 }}
                        onChange={(e) => handlePointChange(type, index, 'address', e.target.value)} />
                    <TextField size="small" placeholder="Giờ đón (VD: 08:00)" value={point.time} sx={{ flex: 1.5 }}
                        onChange={(e) => handlePointChange(type, index, 'time', e.target.value)} />
                    
                    <IconButton color="error" onClick={() => removePoint(type, index)} disabled={form[type].length <= 1}>
                        <RemoveCircleOutlineIcon />
                    </IconButton>
                </Box>
            ))}
        </Box>
    );

    const rowStyle = { display: 'flex', gap: 2, width: '100%', marginBottom: 1 };
    const itemStyle = { flex: 1 };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#ed6c02', color: 'white', fontWeight: 'bold' }}>
                    {initialData ? 'CẬP NHẬT TUYẾN XE' : 'THÊM MỚI TUYẾN XE'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box>
                        {/* DÒNG 1 */}
                        <Box sx={rowStyle}>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Mã Tuyến (Tự động)" />
                                <TextField name="route_code" size="small" fullWidth disabled placeholder="Tự động sinh" value={form.route_code} />
                            </Box>
                            <Box sx={{ flex: 2 }}>
                                <CustomLabel title="Tên Tuyến" required />
                                <TextField name="route_name" size="small" fullWidth required placeholder="VD: Hà Nội - Hạ Long (Limousine)" value={form.route_name} onChange={handleChange} />
                            </Box>
                        </Box>

                        {/* DÒNG 2 */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Nhà xe (NCC)" required />
                                <Autocomplete size="small" options={suppliers} getOptionLabel={(opt) => opt.name || ''}
                                    value={suppliers.find(s => s.id === form.supplier) || null}
                                    onChange={(e, val) => setForm({...form, supplier: val ? val.id : null})}
                                    renderInput={(params) => <TextField {...params} placeholder="Chọn nhà xe..." />} />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Loại xe" required />
                                <TextField select name="vehicle_type" size="small" fullWidth value={form.vehicle_type} onChange={handleChange}>
                                    {VEHICLE_TYPE_CHOICES.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                </TextField>
                            </Box>
                        </Box>

                        {/* DÒNG 3: CÁC ĐIỂM ĐÓN TRẢ */}
                        {renderPointList('pickup_point', 'Danh sách Điểm Đón')}
                        {renderPointList('dropoff_point', 'Danh sách Điểm Trả')}

                        {/* DÒNG 4: GIÁ TIỀN */}
                        <Box sx={{ ...rowStyle, marginBottom: 0 }}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Giá Net (Cost)" />
                                <CurrencyInput value={{original: form.cost_original, currency: form.cost_currency, rate: form.cost_rate, vnd: form.cost_price}} onChange={(data) => handleMoneyChange('cost', data)} />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Giá Bán lẻ (Selling)" />
                                <CurrencyInput value={{original: form.selling_original, currency: form.selling_currency, rate: form.selling_rate, vnd: form.selling_price}} onChange={(data) => handleMoneyChange('selling', data)} />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#ed6c02' }}>Lưu dữ liệu</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={toast.open} autoHideDuration={2000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default TransportRouteDialog;